!function() {
  "use strict";
  
  const selectors = {
    image: '[data-image-id]',
    productImages: 'product-images',
    section: '[data-section-id]',
    thumbItem: '[data-thumb-item]',
    thumbLink: '[data-thumb-link]',
    thumbsSlider: '[data-thumbs-slider]',
    productMediaList: '[data-product-media-list]',
    productSlide: '.product__slide'
  };
  
  const attributes = {
    activeMedia: 'data-active-media',
    mediaId: 'data-media-id',
    imageId: 'data-image-id'
  };
  
  const classes = {
    active: 'is-active'
  };
  
  if (!customElements.get('product-thumbs')) {
    customElements.define('product-thumbs', class extends HTMLElement {
      constructor() {
        super();
        // Minimal fix: no DOM queries in constructor
        this.container = null;
        this.productImages = null;
        this.productImagesContainer = null;
        this.productThumbs = null;
        this.thumbSlider = null;
        this.thumbLinks = null;
        this.productMediaList = null;
      }
      
      connectedCallback() {
        // Minimal fix: do queries only when connected
        this.container = this.closest(selectors.section);
        if (!this.container) return;

        this.productImages = this.container.querySelectorAll(selectors.image);
        this.productImagesContainer = this.container.querySelector(selectors.productImages);
        this.productThumbs = this.container.querySelectorAll(selectors.thumbItem);
        this.thumbSlider = this.querySelector(selectors.thumbsSlider);
        this.thumbLinks = this.querySelectorAll(selectors.thumbLink);
        this.productMediaList = this.container.querySelector(selectors.productMediaList);

        if (!this.productImagesContainer) return;

        this.handleEvents();
        this.preloadImagesOnHover();
        this.activeMediaObserver();
        this.initScrollListener();
        this.initDragToScroll();
      }
      
      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
        }
        if (this.productMediaList) {
          this.productMediaList.removeEventListener('scroll', this.handleProductScrollBound);
          this.removeDragToScroll();
        }
      }
      
      activeMediaObserver() {
        this.observer = new MutationObserver(mutations => {
          for (const mutation of mutations) {
            if (mutation.type === 'attributes' && mutation.attributeName === attributes.activeMedia) {
              this.setActiveThumb();
            }
          }
        });
        
        this.observer.observe(this.productImagesContainer, {
          attributes: true,
          childList: false,
          subtree: false
        });
      }
      
      initScrollListener() {
        if (!this.productMediaList) return;
        
        this.handleProductScrollBound = this.handleProductScroll.bind(this);
        this.productMediaList.addEventListener('scroll', this.handleProductScrollBound);
      }
      
      handleProductScroll() {
        if (this.scrollTimeout) {
          cancelAnimationFrame(this.scrollTimeout);
        }
        
        this.scrollTimeout = requestAnimationFrame(() => {
          this.updateActiveThumbFromScroll();
        });
      }
      
      updateActiveThumbFromScroll() {
        if (!this.productMediaList) return;
        
        const slides = this.productMediaList.querySelectorAll(selectors.productSlide);
        if (!slides.length) return;
        
        const scrollLeft = this.productMediaList.scrollLeft;
        const slideWidth = slides[0].offsetWidth;
        const currentIndex = Math.round(scrollLeft / slideWidth);
        
        // Update active thumb
        this.productThumbs.forEach((thumb, index) => {
          thumb.classList.toggle(classes.active, index === currentIndex);
        });
      }
      
      handleEvents() {
        // Single click handler on the thumb wrapper. Works for both display modes:
        // - Standard thumbnails: user clicks the <a class="product__thumb__link"> and
        //   the event bubbles up to .product__thumb
        // - Dotted navigation: the link is visually hidden, user clicks .product__thumb directly
        // We always dispatch `theme:media:select` so the fader layouts
        // (image_layout=thumbnails / thumbnails-left / mobile_image_style=thumbs)
        // swap the active slide; additionally we call scrollToSlideByIndex() so the
        // scrolling layouts (mobile slider, stacked, grid) scroll to the right slide.
        this.productThumbs?.forEach((thumb, index) => {
          const link = thumb.querySelector(selectors.thumbLink);
          const mediaId = link?.getAttribute(attributes.mediaId);

          thumb.addEventListener('click', (e) => {
            e.preventDefault();

            if (thumb.classList.contains(classes.active)) return;

            if (mediaId) {
              this.dispatchEvent(new CustomEvent('theme:media:select', {
                bubbles: true,
                detail: { id: mediaId }
              }));
            }

            this.scrollToSlideByIndex(index);
          });

          link?.addEventListener('keyup', (e) => {
            if (e.code !== 'Enter' || !mediaId) return;

            const focusElement = this.productImagesContainer
              ?.querySelector(`[${attributes.mediaId}="${mediaId}"]`)
              ?.querySelectorAll('model-viewer, video, iframe, button, [href], input, [tabindex]:not([tabindex="-1"])')[0];

            if (focusElement) {
              focusElement.dispatchEvent(new Event('focus'));
              focusElement.dispatchEvent(new Event('select'));
            }
          });
        });
      }
      
      scrollToSlideByIndex(index) {
        if (!this.productMediaList) return;
        
        const slides = this.productMediaList.querySelectorAll(selectors.productSlide);
        if (!slides[index]) return;
        
        const targetSlide = slides[index];
        this.productMediaList.scrollTo({
          left: targetSlide.offsetLeft,
          behavior: 'smooth'
        });
        
        // Update active thumb immediately
        this.productThumbs.forEach((thumb, i) => {
          thumb.classList.toggle(classes.active, i === index);
        });
      }
      
      preloadImagesOnHover() {
        this.thumbLinks?.forEach((link) => {
          link.addEventListener('mouseover', () => {
            const mediaId = link.getAttribute(attributes.mediaId);
            const image = this.productImagesContainer
              .querySelector(`[${attributes.mediaId}="${mediaId}"] img`);
            
            if (image) {
              image.setAttribute('loading', 'eager');
            }
          });
        });
        
        // Also handle hover on thumb items (dots)
        this.productThumbs?.forEach((thumb, index) => {
          thumb.addEventListener('mouseover', () => {
            const slides = this.productMediaList?.querySelectorAll(selectors.productSlide);
            if (slides && slides[index]) {
              const image = slides[index].querySelector('img');
              if (image) {
                image.setAttribute('loading', 'eager');
              }
            }
          });
        });
      }
      
      setActiveThumb() {
        const activeMediaId = this.productImagesContainer.getAttribute(attributes.activeMedia);
        const activeThumbLink = this.querySelector(`[${attributes.mediaId}="${activeMediaId}"]`);
        
        // Remove active class from all thumbs
        this.querySelector(`${selectors.thumbItem}.${classes.active}`)?.classList.remove(classes.active);
        
        // Add active class to the corresponding thumb
        if (activeThumbLink) {
          activeThumbLink.parentNode.classList.add(classes.active);
        }
        
        requestAnimationFrame(() => {
          this.scrollToThumb();
        });
      }
      
      scrollToThumb() {
        const slider = this.thumbSlider;
        if (!slider) return;
        
        const activeThumb = slider.querySelector(`.${classes.active}`);
        if (!activeThumb) return;
        
        const scrollTop = slider.scrollTop;
        const scrollLeft = slider.scrollLeft;
        const sliderWidth = slider.offsetWidth;
        const sliderHeight = slider.offsetHeight;
        const scrollBottom = scrollTop + sliderHeight;
        const scrollRight = scrollLeft + sliderWidth;
        
        const thumbTop = activeThumb.offsetTop;
        const thumbLeft = activeThumb.offsetLeft;
        const thumbWidth = activeThumb.offsetWidth;
        const thumbHeight = activeThumb.offsetHeight;
        
        const isAbove = scrollTop > thumbTop;
        const isBelow = thumbLeft < scrollLeft;
        const isRight = thumbLeft + thumbWidth > scrollRight;
        const needsVerticalScroll = thumbTop + thumbHeight > scrollBottom || isAbove;
        const needsHorizontalScroll = isRight || isBelow;
        
        const isMobile = window.theme.isMobile();
        
        if (needsVerticalScroll || needsHorizontalScroll) {
          let newScrollTop = thumbTop - sliderHeight + thumbHeight;
          let newScrollLeft = thumbLeft - sliderWidth + thumbWidth;
          
          if (isAbove) {
            newScrollTop = thumbTop;
          }
          
          if (isRight && isMobile) {
            newScrollLeft += parseInt(window.getComputedStyle(slider).paddingRight);
          }
          
          if (isBelow) {
            newScrollLeft = thumbLeft;
            if (isMobile) {
              newScrollLeft -= parseInt(window.getComputedStyle(slider).paddingLeft);
            }
          }
          
          slider.scrollTo({
            top: newScrollTop,
            left: newScrollLeft,
            behavior: 'smooth'
          });
        }
      }
      
      initDragToScroll() {
        if (!this.productMediaList) return;
        
        let isDown = false;
        let startX;
        let scrollLeft;
        
        this.handleMouseDownBound = (e) => {
          isDown = true;
          startX = e.pageX - this.productMediaList.offsetLeft;
          scrollLeft = this.productMediaList.scrollLeft;
          this.productMediaList.style.scrollBehavior = 'auto';
        };
        
        this.handleMouseLeaveBound = () => {
          isDown = false;
          this.productMediaList.style.scrollBehavior = 'smooth';
        };
        
        this.handleMouseUpBound = () => {
          isDown = false;
          this.productMediaList.style.scrollBehavior = 'smooth';
        };
        
        this.handleMouseMoveBound = (e) => {
          if (!isDown) return;
          e.preventDefault();
          const x = e.pageX - this.productMediaList.offsetLeft;
          const walk = (x - startX) * 2; // Scroll speed multiplier
          this.productMediaList.scrollLeft = scrollLeft - walk;
        };
        
        this.productMediaList.addEventListener('mousedown', this.handleMouseDownBound);
        this.productMediaList.addEventListener('mouseleave', this.handleMouseLeaveBound);
        this.productMediaList.addEventListener('mouseup', this.handleMouseUpBound);
        this.productMediaList.addEventListener('mousemove', this.handleMouseMoveBound);
      }
      
      removeDragToScroll() {
        if (!this.productMediaList) return;
        
        this.productMediaList.removeEventListener('mousedown', this.handleMouseDownBound);
        this.productMediaList.removeEventListener('mouseleave', this.handleMouseLeaveBound);
        this.productMediaList.removeEventListener('mouseup', this.handleMouseUpBound);
        this.productMediaList.removeEventListener('mousemove', this.handleMouseMoveBound);
      }
    });
  }
}();
